import { useEffect, useState } from "react";
import adminApi, { fmtDate } from "../../lib/adminApi";
import { formatErr } from "../../lib/api";
import { UserPlus, Trash, Star } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { toast } from "sonner";

export default function AdminAdmins({ me }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", is_super: false });
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/admin/admins").then((r) => setAdmins(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/admin/admins", form);
      toast.success("Администратор добавлен");
      setForm({ email: "", password: "", name: "", is_super: false });
      load();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    try { await adminApi.delete(`/admin/admins/${a.id}`); toast.success("Доступ отозван"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="admin-admins">
      <h2 className="text-lg font-semibold text-white mb-4">Администраторы</h2>

      <form onSubmit={create} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 mb-4 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <label className="text-[10px] tracking-[0.18em] text-[#6B7280] uppercase">Имя</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
            placeholder="Помощник" className="mt-1 h-10 bg-[#0A0E17] border-[#1F2937] text-white rounded-lg" data-testid="new-admin-name" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.18em] text-[#6B7280] uppercase">Email</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
            placeholder="helper@arenatb.com" className="mt-1 h-10 bg-[#0A0E17] border-[#1F2937] text-white rounded-lg" data-testid="new-admin-email" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.18em] text-[#6B7280] uppercase">Пароль</label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6}
            placeholder="мин. 6 симв." className="mt-1 h-10 bg-[#0A0E17] border-[#1F2937] text-white rounded-lg" data-testid="new-admin-password" />
        </div>
        <Button type="submit" disabled={busy} className="h-10 bg-[#E3B341] hover:bg-[#d3a52f] text-[#0A0E17] font-semibold" data-testid="new-admin-submit">
          <UserPlus size={16} className="mr-1" /> Добавить
        </Button>
        <label className="sm:col-span-4 flex items-center gap-2 text-xs text-[#9CA3AF]">
          <Switch checked={form.is_super} onCheckedChange={(v) => setForm({ ...form, is_super: v })} data-testid="new-admin-super" />
          Супер-админ (может управлять другими администраторами)
        </label>
      </form>

      <div className="bg-[#111827] border border-[#1F2937] rounded-xl divide-y divide-[#1F2937]/60">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`admin-row-${a.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{a.name || "—"}</span>
                {a.super && <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#E3B341]/15 text-[#E3B341]"><Star size={10} weight="fill" /> SUPER</span>}
                {a.id === me?.id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-[#6B7280]">это вы</span>}
              </div>
              <p className="text-[11px] text-[#6B7280]">{a.email} · {fmtDate(a.created_at)}</p>
            </div>
            {me?.super && a.id !== me?.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-8 text-[#F6465D] hover:bg-[#F6465D]/10" data-testid={`admin-remove-${a.id}`}>
                    <Trash size={15} className="mr-1" /> Отозвать
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Отозвать доступ администратора?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9CA3AF]">
                      «{a.name || a.email}» потеряет доступ к панели. Аккаунт останется как обычный пользователь.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-[#1F2937] text-white hover:bg-white/5">Отмена</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(a)} className="bg-[#F6465D] hover:bg-[#d93a4f] text-white" data-testid={`admin-remove-confirm-${a.id}`}>
                      Отозвать
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
